import {useRef,useMemo,useEffect} from 'react';
import {Vector3, Matrix4, Quaternion, BufferGeometry, Float32BufferAttribute, DoubleSide} from 'three';
import {useFrame,useThree} from '@react-three/fiber'
import {mergeBufferGeometries} from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import {calculateRotQuat} from './visualmath';
import {useAnimStore} from './index';

export const DeformingElement = (props) => {

	// const timeSteps = 11;
	// const lineSteps = 8;
	// const crossSteps = 1;

	const timeSteps = props.visSettings.timeSteps;
	const lineSteps = props.visSettings.lineSteps;
	const crossSteps = props.visSettings.crossSteps;
	const rate = props.visSettings.rate;

	//initial
	const locY = new Vector3().fromArray(props.locY);
	const initQuat = useMemo(()=>calculateRotQuat(props.coord,locY), [props.coord,locY]);
	
	let rp0 = props.rp0;
	let rq0 = props.rq0;
	let qp0 = initQuat;
	let qq0 = initQuat;

	//as we might change the deltas (scale them), make sure to copy the arrays (and not modify old)
	let dqp = [...props.dcoord.dqp];
	let dqq = [...props.dcoord.dqq];

	//construct quaternion describing the rotated *node*. dqp and dqq are increments in euler angles, so divide by 2
	let dquatp = new Quaternion(dqp[0]/2, dqp[1]/2, dqp[2]/2, 1).normalize();
	let dquatq = new Quaternion(dqq[0]/2, dqq[1]/2, dqq[2]/2, 1).normalize();

	
	//construct displacement vector in fully deformed state
	let rp1 = new Vector3().addVectors(rp0, props.dcoord.drp);
	let rq1 = new Vector3().addVectors(rq0, props.dcoord.drq);

	// let l0 = initQuat.w;
	// let l1 = initQuat.x;
	// let l2 = initQuat.y;
	// let l3 = initQuat.z;

	// let Q1 = matrix([
	// 	[l0, -l1, -l2, -l3],
	// 	[l1, l0, -l3, l2],
	// 	[l2, l3, l0, -l1],
	// 	[l3, -l2, l1, l0],
	// ]);
	// let Q2 = matrix([
	// 	[l0, -l1, -l2, -l3],
	// 	[l1, l0, l3, -l2],
	// 	[l2, -l3, l0, l1],
	// 	[l3, l2, -l1, l0],
	// ]);

	// let dap = matrix([0,dqp[0],dqp[1],dqp[2]]);
	// let daq = matrix([0,dqq[0],dqq[1],dqq[2]]);

	// let dlabp = multiply(Q2,dap);
	// let dlabq = multiply(Q2,daq);
	
	// dquatp = new Quaternion(
	// 			2*dlabp.get([1]),
	// 			2*dlabp.get([2]),
	// 			2*dlabp.get([3]),
	// 			2*dlabp.get([0])
	// 		);
	// dquatq = new Quaternion(
	// 			2*dlabq.get([1]),
	// 			2*dlabq.get([2]),
	// 			2*dlabq.get([3]),
	// 			2*dlabq.get([0])
	// 		);

	// console.log(dlabp)
	// console.log(dlabp.get([0]))
	// console.log(dlabp.get([1]))
	// console.log(dlabp.get([2]))
	// console.log(dlabp.get([3]))

	//construct quaternion describing the rotated cross-section of *this element*	
	let qp1 = new Quaternion().multiplyQuaternions(dquatp,initQuat);
	let qq1 = new Quaternion().multiplyQuaternions(dquatq,initQuat);

	// let qp1 = new Quaternion().multiplyQuaternions(initQuat,dquatp);
	// let qq1 = new Quaternion().multiplyQuaternions(initQuat,dquatq);

	// let qp1 = new Quaternion(
	// 	initQuat.x+2*dlabp.get([1]),
	// 	initQuat.y+2*dlabp.get([2]),
	// 	initQuat.z+2*dlabp.get([3]),
	// 	initQuat.w+2*dlabp.get([0])
	// 	);
	// let qq1 = new Quaternion(
	// 	initQuat.x+2*dlabq.get([1]),
	// 	initQuat.y+2*dlabq.get([2]),
	// 	initQuat.z+2*dlabq.get([3]),
	// 	initQuat.w+2*dlabq.get([0])
	// 	);

	//holder for intermediate (interpolated) values
	let rp = new Vector3();
	let rq = new Vector3();
	let qp = new Quaternion();
	let qq = new Quaternion();

	let geomArray = [];
	var leafGeom = [];
	//positive direction
	for (let i = 0; i < timeSteps; i++){
		let sc = i/(timeSteps-1);
		
		rp.lerpVectors(rp0,rp1,sc);
		rq.lerpVectors(rq0,rq1,sc);
		qp.slerpQuaternions(qp0,qp1,sc);
		qq.slerpQuaternions(qq0,qq1,sc);

		leafGeom = leaf(rp,rq,qp,qq,props.style.t,props.style.w,lineSteps,crossSteps);			   		
		geomArray.push(leafGeom.geometry);
	}

	rp1 = new Vector3().subVectors(rp0, props.dcoord.drp);
	rq1 = new Vector3().subVectors(rq0, props.dcoord.drq);
	qp1 = new Quaternion().multiplyQuaternions(dquatp.conjugate(),initQuat);
	qq1 = new Quaternion().multiplyQuaternions(dquatq.conjugate(),initQuat);

	//negative direction
	for (let i = 0; i < timeSteps; i++){
		let sc = i/(timeSteps-1);
		
		rp.lerpVectors(rp0,rp1,sc);
		rq.lerpVectors(rq0,rq1,sc);
		qp.slerpQuaternions(qp0,qp1,sc);
		qq.slerpQuaternions(qq0,qq1,sc);

		leafGeom = leaf(rp,rq,qp,qq,props.style.t,props.style.w,lineSteps,crossSteps);			   		
		geomArray.push(leafGeom.geometry);
	}

	let totalGeom = mergeBufferGeometries(geomArray);
	
	let time = 0;
	let idx = 0;
	let getIndex = (delta) => {
		time += delta;
		// return Math.round( (Math.sin(time * rate) +1)/2*(timeSteps-1) ); //old: for only the positive direction
		idx = Math.round( Math.cos(time*rate)*(timeSteps-1) );
		if(idx < 0){
			idx = -idx+timeSteps; //flip sign to make positive, and offset with timeSteps, to go to the geometries for the negative direction
		}
		return idx;
		
	};

    //---piece of code that uses the custom animation store for faster access---
    //get show state, store in reference toggleRef, use in useFrame
    const toggleRef = useRef(useAnimStore.getState().show);
    useEffect( () => useAnimStore.subscribe(
     show => {
     	toggleRef.current = show
     },
     state => state.show
     ), [])
    //---end of animation store code----

	const {invalidate} = useThree();

	let iiPrev = 0;
	let iiNow = 0;
	useFrame( (state,delta)=>{ 
	  invalidate();
	  if(toggleRef.current){
		iiNow = getIndex(delta);
		if (iiNow !== iiPrev) {
			totalGeom.setDrawRange(3*leafGeom.vertexCount*(iiNow), 3*leafGeom.vertexCount);
			iiPrev = iiNow;
			// console.log(iiNow);
		}

	  }
	});

    //set initial loadstep, to be shown upon loading page
    useEffect(()=>{
     totalGeom.setDrawRange(3*leafGeom.vertexCount*(timeSteps-1), 3*leafGeom.vertexCount);
     invalidate();
    })

    // const meshRef = useRef();
    
   //  useEffect(() => {
	  //   meshRef.current.addEventListener('change', invalidate)
	  //   // return () => meshRef.removeEventListener('change', invalidate)
	  //   // console.log(meshRef)
	  // }, [])

	//old green color: 0x44aa88
	return (
		<mesh geometry={totalGeom}>
			<meshPhongMaterial color={props.style.color} side={DoubleSide}/>
		</mesh>
	);
}

const leaf = function (rp,rq,qp,qq,w,t,lineSteps,crossSteps) {

	var getIndices = function (lineSteps,crossSteps,offset){

		let indices = [];
		const crossStepCount = crossSteps + 1;
	   	
	   	for ( let i = 0; i < lineSteps; i ++ ) {

			for ( let j = 0; j < crossSteps; j ++ ) {

				const a = i * crossStepCount + j + offset;
				const b = i * crossStepCount + j + 1 + offset;
				const c = ( i + 1 ) * crossStepCount + j + 1 + offset;
				const d = ( i + 1 ) * crossStepCount + j + offset;

				// faces one and two
				indices.push( a, b, d );
				indices.push( b, c, d );
			}
		}

		let maxIndex = lineSteps*(crossSteps + 1) + crossSteps + offset;
		let numIndices = lineSteps*crossSteps*2;

		return {indices:indices, maxIndex:maxIndex, numIndices:numIndices};
	}

	var topSurfPos = function (a,b) {
	   
		var x = l.x + nyb.x*t/2 + nzb.x*w*(a-1/2);
	   	var y = l.y + nyb.y*t/2 + nzb.y*w*(a-1/2);		
	   	var z = l.z + nyb.z*t/2 + nzb.z*w*(a-1/2);

	   	return new Vector3(x,y,z);

	}	

	var bottomSurfPos = function (a,b) {
	   
		var x = l.x - nyb.x*t/2 + nzb.x*w*(a-1/2);
		var y = l.y - nyb.y*t/2 + nzb.y*w*(a-1/2);
		var z = l.z - nyb.z*t/2 + nzb.z*w*(a-1/2);

	   	return new Vector3(x,y,z);

	}	

	var leftSurfPos = function (a,b) {
	   
	   var x = l.x - nyb.x*t*(a-1/2) - nzb.x*w/2;
	   var y = l.y - nyb.y*t*(a-1/2) - nzb.y*w/2;
	   var z = l.z - nyb.z*t*(a-1/2) - nzb.z*w/2;

	   return new Vector3(x,y,z);

	}	

	var rightSurfPos = function (a,b) {
	   
	   var x = l.x - nyb.x*t*(a-1/2) + nzb.x*w/2;
	   var y = l.y - nyb.y*t*(a-1/2) + nzb.y*w/2;
	   var z = l.z - nyb.z*t*(a-1/2) + nzb.z*w/2;

	   return new Vector3(x,y,z);

	}

	var frontSurf = function (a, b) {
			   
	   var x = rp.x + (-t/2 + t*b)*nyp.x + (-w/2 + w*a)*nzp.x;
	   var y = rp.y + (-t/2 + t*b)*nyp.y + (-w/2 + w*a)*nzp.y;
	   var z = rp.z + (-t/2 + t*b)*nyp.z + (-w/2 + w*a)*nzp.z;

	   return new Vector3(x,y,z);;

	}


	var backSurf = function (a, b) {
	   
	   var x = rq.x + (-t/2 + t*b)*nyq.x + (-w/2 + w*a)*nzq.x;
	   var y = rq.y + (-t/2 + t*b)*nyq.y + (-w/2 + w*a)*nzq.y;
	   var z = rq.z + (-t/2 + t*b)*nyq.z + (-w/2 + w*a)*nzq.z;

	   return new Vector3(x,y,z);;

	}	

	let vertices = [];
	vertices.top = [];
	vertices.bottom = [];
	vertices.left = [];
	vertices.right = [];
	vertices.front = [];
	vertices.back = [];
	
	let uvs = [];
	uvs.top = [];
	uvs.bottom = [];
	uvs.left = [];
	uvs.right = [];
	uvs.front = [];
	uvs.back = [];
	
	let normals = [];
	normals.top = [];
	normals.bottom = [];
	normals.left = [];
	normals.right = [];
	normals.front = [];
	normals.back = [];

	let el = new Vector3();
	let L = el.subVectors(rq,rp).length();
  
   let rotp = new Matrix4();
   let nxp = new Vector3();
   let nyp = new Vector3();
   let nzp = new Vector3();

   let rotq = new Matrix4();
   let nxq = new Vector3();
   let nyq = new Vector3();
   let nzq = new Vector3();

   // ----------
   // interpolate elastic line
   // ----------
   // get rotation matrices at nodes
   rotp.makeRotationFromQuaternion(qp);
   rotp.extractBasis(nxp,nyp,nzp);
   
   rotq.makeRotationFromQuaternion(qq);
   rotq.extractBasis(nxq,nyq,nzq);

   //interpolate quaternions
   let qb = new Quaternion();
   let rotb = new Matrix4();
   let nxb = new Vector3();
   let nyb = new Vector3();
   let nzb = new Vector3();
   let l = new Vector3();

   for (let i=0; i<=lineSteps; i++) {

   		let b = i/lineSteps;
		let p1 = 2*b**3 - 3*b**2 + 1;
		let p2 = b**3 -2*b**2 + b;
		let p3 = -2*b**3+3*b**2;
		let p4 = b**3 - b**2;

		l.setComponent(0, p1*rp.x + L*p2*nxp.x + p3*rq.x + L*p4*nxq.x); //set x of vector l to ..
   		l.setComponent(1, p1*rp.y + L*p2*nxp.y + p3*rq.y + L*p4*nxq.y); //set y of vector l to ..
   		l.setComponent(2, p1*rp.z + L*p2*nxp.z + p3*rq.z + L*p4*nxq.z); //set z of vector l to ..

		qb.slerpQuaternions(qp,qq,b);
		rotb.makeRotationFromQuaternion(qb);
		rotb.extractBasis(nxb,nyb,nzb);

		for (let j=0; j<=crossSteps; j++){

			let a = j/crossSteps;

			let p = topSurfPos(a,b);
			let normal = nyb;
			vertices.top.push(p.x,p.y,p.z);
			normals.top.push( normal.x, normal.y, normal.z );
			uvs.top.push( a, b );

			p = bottomSurfPos(a,b);
			normal = nyb;
			vertices.bottom.push(p.x,p.y,p.z);
			normals.bottom.push( normal.x, normal.y, normal.z );
			uvs.bottom.push( a, b );

			p = leftSurfPos(a,b);
			normal = nzb;
			vertices.left.push(p.x,p.y,p.z);
			normals.left.push( normal.x, normal.y, normal.z );
			uvs.left.push( a, b );

			p = rightSurfPos(a,b);
			normal = nzb;
			vertices.right.push(p.x,p.y,p.z);
			normals.right.push( normal.x, normal.y, normal.z );
			uvs.right.push( a, b );

		}

   }

	// front and back faces
	let tSteps = 2;
	let wSteps = 2;
	for (let i=0; i<=tSteps; i++){
		let b = i/tSteps;
		for (let j=0; j<=wSteps; j++){
			let a = j/wSteps;
			
			let p = frontSurf(a,b);
			let normal = nxp;
			vertices.front.push(p.x,p.y,p.z);
			normals.front.push(normal.x,normal.y,normal.z);
			uvs.front.push(a,b);

			p = backSurf(a,b);
			normal = nxq;
			vertices.back.push(p.x,p.y,p.z);
			normals.back.push(normal.x,normal.y,normal.z);
			uvs.back.push(a,b);
		}
	}

	// let offset = lineSteps*(crossSteps+1)+crossSteps+1; //counts the highest vertex index

	let indicesCollect = [];
	let indices = [];
	let numIndices = [];
	let maxIndex = [];
	let vertexCount = 0; //total vertex count

	//top
	( {indices,numIndices,maxIndex} = getIndices(lineSteps, crossSteps, 0));
	indicesCollect.push(...indices);
	vertexCount += numIndices;

	//bottom
	( {indices,numIndices,maxIndex} = getIndices(lineSteps, crossSteps, maxIndex+1));
	indicesCollect.push(...indices);
	vertexCount += numIndices;
	
	//left
	( {indices,numIndices,maxIndex} = getIndices(lineSteps, crossSteps, maxIndex+1));
	indicesCollect.push(...indices);
	vertexCount += numIndices;
	
	//right
	( {indices,numIndices,maxIndex} = getIndices(lineSteps, crossSteps, maxIndex+1));
	indicesCollect.push(...indices);
	vertexCount += numIndices;
	
	//front
	( {indices,numIndices,maxIndex} = getIndices(wSteps, tSteps, maxIndex+1));
	indicesCollect.push(...indices);
	vertexCount += numIndices;

	//back
	( {indices,numIndices,maxIndex} = getIndices(wSteps, tSteps, maxIndex+1));
	indicesCollect.push(...indices);
	vertexCount += numIndices;

	const geometry = new BufferGeometry();
	geometry.setIndex(indicesCollect);
	geometry.setAttribute('position', new Float32BufferAttribute([...vertices.top,...vertices.bottom,...vertices.left,...vertices.right,...vertices.front,...vertices.back],3) );
	geometry.setAttribute('normal', new Float32BufferAttribute([...normals.top,...normals.bottom,...normals.left,...normals.right,...normals.front,...normals.back],3) );
	// geometry.setAttribute('uv', new Float32BufferAttribute([...uvs.top, ...uvs.bottom],2) );

	return {geometry:geometry, vertexCount:vertexCount};
	
}